const TOKEN_KEY = "token";
const AUTH_SYNC_CHANNEL = "rabiitrip_auth_sync";

let sessionGeneration = 0;
let memoryToken = null;
let initialized = false;
let storageWriteFailed = false;

export const BROADCAST_SENDER_ID =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

let authChannel = null;
try {
  if (typeof BroadcastChannel !== "undefined") {
    authChannel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
  }
} catch (_error) {
  authChannel = null;
}

function tokenStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (_error) {
    return null;
  }
}

function readStorageToken() {
  try {
    return { ok: true, token: tokenStorage()?.getItem(TOKEN_KEY) || null };
  } catch (_error) {
    return { ok: false, token: memoryToken };
  }
}

function reconcileStoredToken() {
  if (storageWriteFailed) {
    initialized = true;
    return memoryToken;
  }
  const stored = readStorageToken();
  if (!initialized) {
    initialized = true;
    memoryToken = stored.token;
  } else if (stored.ok && stored.token !== memoryToken) {
    memoryToken = stored.token;
    sessionGeneration += 1;
  }
  return memoryToken;
}

export function getSessionSnapshot() {
  const token = reconcileStoredToken();
  return {
    generation: sessionGeneration,
    token,
  };
}

export function getStoredSessionToken() {
  return getSessionSnapshot().token;
}

export function commitSessionToken(token) {
  const previousMemoryToken = reconcileStoredToken();
  const normalizedToken = typeof token === "string" && token ? token : null;
  initialized = true;
  memoryToken = normalizedToken;
  sessionGeneration += 1;
  try {
    const storage = tokenStorage();
    if (normalizedToken) storage?.setItem(TOKEN_KEY, normalizedToken);
    else storage?.removeItem(TOKEN_KEY);
    storageWriteFailed = false;
  } catch (_error) {
    // Keep the in-memory session coherent when browser storage is unavailable.
    storageWriteFailed = true;
  }
  if (normalizedToken !== previousMemoryToken) {
    try {
      authChannel?.postMessage({
        type: "session_change",
        nextToken: normalizedToken,
        previousToken: previousMemoryToken,
        sender: BROADCAST_SENDER_ID,
      });
    } catch (_error) {
      // Ignore broadcast errors
    }
  }
  return getSessionSnapshot();
}

export function isCurrentSessionSnapshot(snapshot) {
  if (!snapshot || !snapshot.token) return false;
  const current = getSessionSnapshot();
  return (
    current.generation === snapshot.generation &&
    current.token === snapshot.token
  );
}

export function clearSessionIfCurrent(snapshot) {
  if (!isCurrentSessionSnapshot(snapshot)) return false;
  commitSessionToken(null);
  return true;
}

function authorizationHeader(headers) {
  if (!headers) return null;
  if (typeof headers.get === "function") {
    return headers.get("Authorization") || headers.get("authorization") || null;
  }
  const key = Object.keys(headers).find(
    (name) => name.toLowerCase() === "authorization",
  );
  return key ? headers[key] : null;
}

function setAuthorizationHeader(config, value) {
  config.headers ||= {};
  if (typeof config.headers.set === "function") {
    config.headers.set("Authorization", value);
  } else {
    config.headers.Authorization = value;
  }
}

function bearerToken(value) {
  if (typeof value !== "string") return null;
  const match = value.match(/^Bearer\s+(.+)$/i);
  return match?.[1] || null;
}

export function decorateSessionRequest(config = {}) {
  const controls = config.rabiitripSession || {};
  const snapshot = getSessionSnapshot();
  const explicitToken = bearerToken(authorizationHeader(config.headers));
  const token = explicitToken || (controls.skipAuth ? null : snapshot.token);

  if (!explicitToken && token) {
    setAuthorizationHeader(config, `Bearer ${token}`);
  }

  config.rabiitripSession = {
    ...controls,
    generation: snapshot.generation,
    tokenSnapshot: token,
    authenticated: Boolean(token),
    tokenSource: explicitToken ? "explicit" : token ? "storage" : "none",
  };
  return config;
}

export function expireRequestSessionIfCurrent(config) {
  const metadata = config?.rabiitripSession;
  if (!metadata?.authenticated || !metadata.tokenSnapshot) return false;
  return clearSessionIfCurrent({
    generation: metadata.generation,
    token: metadata.tokenSnapshot,
  });
}

export function isRequestSessionCurrent(config) {
  const metadata = config?.rabiitripSession;
  if (!metadata?.authenticated && !metadata?.boundary) return true;
  const current = getSessionSnapshot();
  if (current.generation !== metadata.generation) return false;
  return !metadata.authenticated || current.token === metadata.tokenSnapshot;
}

export function withoutSessionAuth(config = {}, boundary = "login") {
  return {
    ...config,
    rabiitripSession: {
      ...(config.rabiitripSession || {}),
      boundary,
      skipAuth: true,
    },
  };
}
